import { motion } from "framer-motion";
import { Building2, Phone, CheckCircle2 } from "lucide-react";
import { formatPhoneOnInput, validateGhanaPhoneNumber } from "../../../utils/formValidators";
import { useState } from "react";

export default function Step1CompanyInfo({ formData, setFormData, errors }) {
  const [phoneValidation, setPhoneValidation] = useState({
    telephoneNumber: null,
    cellNumber: null,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (field, value) => {
    const formatted = formatPhoneOnInput(value);
    handleChange(field, formatted);
    const validation = validateGhanaPhoneNumber(formatted.replace(/\s/g, ""));
    setPhoneValidation((prev) => ({ ...prev, [field]: validation.isValid ? "valid" : null }));
  };

  const err = (f) => (errors?.[f] ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-gray-400");
  const valid = (f) => (phoneValidation[f] === "valid" ? "border-green-300 bg-green-50" : "");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      <div className="flex gap-3 items-center mb-8">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md">
          <Building2 size={20} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Company Information</h2>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-6">
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              Name of Company <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.companyName || ""}
              onChange={(e) => handleChange("companyName", e.target.value)}
              className={"w-full px-4 py-3 text-gray-900 bg-white rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("companyName")}
              placeholder="Enter company name"
            />
            {errors?.companyName && <p className="mt-1 text-sm text-red-500">{errors.companyName}</p>}
          </div>
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <Phone size={16} className="text-purple-600" /> Telephone Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                value={formData.telephoneNumber || ""}
                onChange={(e) => handlePhoneChange("telephoneNumber", e.target.value)}
                className={"w-full px-4 py-3 pr-10 text-gray-900 bg-white rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + (valid("telephoneNumber") || err("telephoneNumber"))}
                placeholder="0241234567 or 233241234567"
              />
              {phoneValidation.telephoneNumber === "valid" && <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />}
            </div>
            <p className="mt-1 text-xs text-gray-500">Format: 233XXXXXXXXX (12 digits) or 0XXXXXXXXX (10 digits)</p>
            {errors?.telephoneNumber && <p className="mt-1 text-sm text-red-500">{errors.telephoneNumber}</p>}
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">Fax Number</label>
            <input
              type="tel"
              value={formData.faxNumber || ""}
              onChange={(e) => handleChange("faxNumber", e.target.value)}
              className="w-full px-4 py-3 text-gray-900 bg-white rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Enter fax number (optional)"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">Website Address</label>
            <input
              type="url"
              value={formData.websiteAddress || ""}
              onChange={(e) => handleChange("websiteAddress", e.target.value)}
              className="w-full px-4 py-3 text-gray-900 bg-white rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="https://www.example.com"
            />
          </div>
        </div>
        <div className="space-y-6">
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              Company VAT Registration Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.vatRegistrationNumber || ""}
              onChange={(e) => handleChange("vatRegistrationNumber", e.target.value)}
              className={"w-full px-4 py-3 text-gray-900 bg-white rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("vatRegistrationNumber")}
              placeholder="Enter VAT registration number"
            />
            {errors?.vatRegistrationNumber && <p className="mt-1 text-sm text-red-500">{errors.vatRegistrationNumber}</p>}
          </div>
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <Phone size={16} className="text-purple-600" /> Cell Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                value={formData.cellNumber || ""}
                onChange={(e) => handlePhoneChange("cellNumber", e.target.value)}
                className={"w-full px-4 py-3 pr-10 text-gray-900 bg-white rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + (valid("cellNumber") || err("cellNumber"))}
                placeholder="0241234567 or 233241234567"
              />
              {phoneValidation.cellNumber === "valid" && <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />}
            </div>
            {errors?.cellNumber && <p className="mt-1 text-sm text-red-500">{errors.cellNumber}</p>}
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              Business Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.businessEmail || ""}
              onChange={(e) => handleChange("businessEmail", e.target.value)}
              className={"w-full px-4 py-3 text-gray-900 bg-white rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("businessEmail")}
              placeholder="business@example.com"
            />
            {errors?.businessEmail && <p className="mt-1 text-sm text-red-500">{errors.businessEmail}</p>}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
